import { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { UserLayout } from '../layout';
import { useAuth } from '../../context/AuthContext';
import { useDomain } from '../../context/DomainContext';
import { getModuleNavMap } from '../../services/modulesService';

// Core component loaded immediately (home page)
import Home from './Home';

// Dashboard also lazy loaded (not always the initial page)
const Dashboard = lazy(() => import('./Dashboard'));

// Lazy loaded modules - only loaded when accessed
const Modules = lazy(() => import('./Modules'));
const ModuleDetailPage = lazy(() => import('./ModuleDetailPage'));

// Contacts module
const SearchContacts = lazy(() => import('../contacts/SearchContacts'));
const MyContacts = lazy(() => import('../contacts/MyContacts'));
const ContactsMap = lazy(() => import('../contacts/ContactsMap'));

// Calendar module
const Calendar = lazy(() => import('../calendar/Calendar'));
const Events = lazy(() => import('../calendar/Events'));

// Other modules - lazy loaded
const EndroitsPage = lazy(() => import('../places/EndroitsPage'));
const Messages = lazy(() => import('../messages/Messages'));
const UserProfile = lazy(() => import('../profile/UserProfile'));
const Preferences = lazy(() => import('../profile/Preferences'));
const Support = lazy(() => import('../support/Support'));
const WorkRequests = lazy(() => import('../work/WorkRequests'));
const WorkOffers = lazy(() => import('../offers/WorkOffers'));
const AdvertisementsPage = lazy(() => import('../advertisements/AdvertisementsPage'));
const WebsitesPage = lazy(() => import('../websites/WebsitesPage'));
const ProductsPage = lazy(() => import('../products/ProductsPage'));
const KnowledgePage = lazy(() => import('../knowledge/KnowledgePage'));
const ExpensesPage = lazy(() => import('../expenses/ExpensesPage'));
const BooksPage = lazy(() => import('../books/BooksPage'));
const MomentsPage = lazy(() => import('../moments/MomentsPage'));
const CarpoolingPage = lazy(() => import('../carpooling/CarpoolingPage'));
const OccupationGroupsPage = lazy(() => import('../occupation-groups/OccupationGroupsPage'));
const ProjectsPage = lazy(() => import('../projects/ProjectsPage'));
const TimeTracking = lazy(() => import('../timetracking/TimeTracking'));
const MeetingsPage = lazy(() => import('../meetings/MeetingsPage'));
const NotificationsPage = lazy(() => import('../notifications/NotificationsPage'));
const NeighborhoodPage = lazy(() => import('../neighborhood/NeighborhoodPage'));
const ActivitiesPage = lazy(() => import('../activities/ActivitiesPage'));

const DASHBOARD_PAGE_KEY = 'cabaza_dashboard_page';

const VALID_PAGES = ['home', 'search', 'modules', 'contacts', 'messages', 'map', 'profile', 'calendar', 'preferences', 'events', 'places', 'support', 'work', 'offers', 'advertisements', 'websites', 'products', 'knowledge', 'expenses', 'books', 'moments', 'carpooling', 'occupationGroups', 'projects', 'timeTracking', 'meetings', 'notifications', 'neighborhood', 'dashboard', 'activities'];

/**
 * Les pages transverses, accessibles depuis une application autonome.
 *
 * Le compte est unique : on doit pouvoir gérer son profil, ses préférences,
 * son abonnement — lieu de la vente croisée — et répondre à un message reçu
 * sur la plateforme, même depuis une adresse d'app.
 */
const PAGES_TRANSVERSES = ['dashboard', 'home', 'profile', 'preferences', 'messages', 'notifications', 'support', 'activities'];

/**
 * Restreint les pages atteignables au périmètre du domaine.
 *
 * Les modules sont en chargement différé, mais seraient montés sur simple
 * changement de page : sans cette barrière, le filtre du menu se contourne
 * par une URL ou une entrée de localStorage laissée par une visite sur la
 * plateforme.
 *
 * @param {string|null} moduleCode - Le module du domaine, ou null pour la plateforme
 * @param {object} navMap - Correspondance code de module → identifiant de page
 */
export const pagesAutoriseesPourDomaine = (moduleCode, navMap) => {
    if (!moduleCode) return VALID_PAGES;

    const code = String(moduleCode).toUpperCase();
    const pageDuModule = (navMap && navMap[code]) || code.toLowerCase();

    return [...PAGES_TRANSVERSES, pageDuModule].filter(p => VALID_PAGES.includes(p));
};

/**
 * Loading fallback component
 */
const PageLoader = () => (
    <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cabaza-purple" />
    </div>
);

/**
 * User Dashboard - Main component for logged-in users
 * Uses React.lazy for code splitting - modules are loaded on demand
 */
const UserDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();

    // Sur un sous-domaine d'app, seules les pages de son module et les
    // transverses sont atteignables. Sur la plateforme, la liste complète.
    const { moduleCode } = useDomain();
    const pagesAutorisees = useMemo(
        () => pagesAutoriseesPourDomaine(moduleCode, getModuleNavMap()),
        [moduleCode]
    );

    // Get initial page from localStorage (persisted across reloads)
    const getInitialPage = () => {
        // Check if page was passed via navigation state (from OccupationsPage, etc.)
        if (location.state?.page) {
            if (pagesAutorisees.includes(location.state.page)) {
                return location.state.page;
            }
        }
        // Une page retenue lors d'une visite sur la plateforme ne doit pas
        // rouvrir un module fermé ici.
        const savedPage = localStorage.getItem(DASHBOARD_PAGE_KEY);
        if (savedPage && pagesAutorisees.includes(savedPage)) {
            return savedPage;
        }
        return 'dashboard';
    };

    const [currentPage, setCurrentPage] = useState(getInitialPage);
    const [entityId, setEntityId] = useState(null);
    const [tabName, setTabName] = useState(null);

    // Handle navigation state changes (when coming from other pages)
    useEffect(() => {
        if (location.state?.page) {
            if (pagesAutorisees.includes(location.state.page)) {
                setCurrentPage(location.state.page);
                // Set entityId if provided (for deep linking from notifications)
                if (location.state.entityId) {
                    setEntityId(location.state.entityId);
                }
                // Set tabName if provided (for navigating to specific tab)
                if (location.state.tabName) {
                    setTabName(location.state.tabName);
                }
                // Clear the state to prevent re-navigation on refresh
                navigate('/', { replace: true, state: {} });
            }
        }
    }, [location.state, navigate]);

    // Clear entityId when page changes (except when coming from notification)
    useEffect(() => {
        // Only clear if not coming from a notification navigation
        if (!location.state?.entityId) {
            setEntityId(null);
        }
    }, [currentPage]);

    // Persist current page to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem(DASHBOARD_PAGE_KEY, currentPage);
    }, [currentPage]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const renderPage = () => {
        switch (currentPage) {
            case 'home':
                return <Home onNavigate={setCurrentPage} />;
            case 'search':
                return <SearchContacts />;
            case 'modules':
                return <Modules onNavigate={setCurrentPage} />;
            case 'contacts':
                return <MyContacts />;
            case 'messages':
                return <Messages />;
            case 'map':
                return <ContactsMap />;
            case 'profile':
                return <UserProfile />;
            case 'calendar':
                return <Calendar language="fr" />;
            case 'events':
                return <Events />;
            case 'places':
                return <EndroitsPage />;
            case 'support':
                return <Support />;
            case 'work':
                return <WorkRequests />;
            case 'offers':
                return <WorkOffers />;
            case 'advertisements':
                return <AdvertisementsPage />;
            case 'websites':
                return <WebsitesPage />;
            case 'products':
                return <ProductsPage />;
            case 'knowledge':
                return <KnowledgePage />;
            case 'expenses':
                return <ExpensesPage />;
            case 'books':
                return (
                    <BooksPage
                        initialTab={tabName}
                        initialEntityId={entityId}
                        onEntityViewed={() => { setEntityId(null); setTabName(null); }}
                        onNavigate={setCurrentPage}
                    />
                );
            case 'moments':
                return <MomentsPage />;
            case 'carpooling':
                return <CarpoolingPage />;
            case 'occupationGroups':
                return <OccupationGroupsPage />;
            case 'projects':
                return <ProjectsPage />;
            case 'timeTracking':
                return <TimeTracking />;
            case 'meetings':
                return <MeetingsPage initialMeetingId={entityId} onMeetingViewed={() => setEntityId(null)} />;
            case 'notifications':
                return <NotificationsPage />;
            case 'neighborhood':
                return <NeighborhoodPage />;
            case 'activities':
                return <ActivitiesPage />;
            case 'dashboard':
                return <Dashboard onNavigate={setCurrentPage} />;
            case 'preferences':
                return (
                    <Preferences
                        onNavigate={(page, moduleCode) => {
                            if (page === 'moduleDetail' && moduleCode) {
                                setEntityId(moduleCode);
                            }
                            setCurrentPage(page);
                        }}
                    />
                );
            case 'moduleDetail':
                return (
                    <ModuleDetailPage
                        moduleCode={entityId}
                        onBack={() => setCurrentPage('preferences')}
                        onNavigate={(page, params) => {
                            if (params?.tab) {
                                localStorage.setItem('cabazaPreferencesMainTab', params.tab);
                            }
                            if (params?.subTab) {
                                localStorage.setItem('cabazaPreferencesModulesSubTab', params.subTab);
                            }
                            setCurrentPage(page);
                        }}
                    />
                );
            default:
                return <Home onNavigate={setCurrentPage} />;
        }
    };

    return (
        <UserLayout
            currentPage={currentPage}
            onNavigate={setCurrentPage}
            onLogout={handleLogout}
        >
            <Suspense fallback={<PageLoader />}>
                {renderPage()}
            </Suspense>
        </UserLayout>
    );
};

export default UserDashboard;
